/**
 * Pure decision helpers for the QuickSearch docs session: which indexer to
 * prefer (local vs remote) and which status banner to surface.
 *
 * Extracted from {@link DocsQuickSearchSession} so the local-only / offline /
 * cloud-fallback contract is testable without constructing the framework-scoped
 * Entity. The session delegates to these; behaviour is unchanged.
 */
import type { I18nString } from '@affine/i18n';
import type { IndexerPreferOptions } from '@affine/nbstore';

/** Snapshot of the session flags the prefer/status decision depends on. */
export interface DocsSearchModeFlags {
  isCloudWorkspace: boolean;
  isOnline: boolean;
  isServerIndexerSupported: boolean;
  isBatterySaveMode: boolean;
  isPdfSearchInCooldown: boolean;
}

/**
 * Only cloud workspaces with an online, indexer-capable server prefer the
 * remote indexer; everything else falls back to the local indexer.
 */
export function resolvePreferredDocsSearchMode(
  flags: DocsSearchModeFlags
): IndexerPreferOptions {
  return !flags.isCloudWorkspace ||
    !flags.isOnline ||
    !flags.isServerIndexerSupported
    ? 'local'
    : 'remote';
}

/**
 * User-facing status banner for the active search mode. `null` means the silent
 * happy path (remote indexer, nothing to warn about).
 */
export function statusForDocsSearchMode(
  flags: DocsSearchModeFlags,
  prefer: IndexerPreferOptions
): I18nString | null {
  if (!flags.isCloudWorkspace) {
    return { i18nKey: 'com.streamlain.quicksearch.status.local-only' };
  }
  if (!flags.isOnline) {
    return { i18nKey: 'com.streamlain.quicksearch.status.offline' };
  }
  if (!flags.isServerIndexerSupported || prefer === 'local') {
    return {
      i18nKey: 'com.streamlain.quicksearch.status.cloud-unavailable',
    };
  }
  if (flags.isBatterySaveMode) {
    return { i18nKey: 'com.streamlain.quicksearch.status.battery-save' };
  }
  if (flags.isPdfSearchInCooldown) {
    return {
      i18nKey: 'com.streamlain.quicksearch.status.pdf-unavailable',
    };
  }
  return null;
}

/**
 * Shown when a remote search failed and the session silently retried locally.
 * This is NOT silent: the user is told cloud search failed.
 */
export const DOCS_SEARCH_CLOUD_FALLBACK_STATUS: I18nString = {
  i18nKey: 'com.streamlain.quicksearch.status.cloud-fallback',
};

export const SEARCH_DEGRADED_STATUS: I18nString = {
  i18nKey: 'com.streamlain.quicksearch.status.cloud-unavailable',
};
